import { ApplianceStates, vSphereVersions, CONFIG, UserRoles } from '../constants';

export const getUserRole = () => {
    let storedUserRole = sessionStorage.getItem('userProjectRole');
    if (storedUserRole) {
        return storedUserRole;
    } else {
        let userRecord = getSessionVar('user') || {};
        let userOrg = sessionStorage.getItem('userOrg');
        let organizations = getSessionVar('organizations');
        let curOrgIndex = (organizations || []).findIndex(function (org) {
            return org.id === userOrg;
        });
        let curOrg = curOrgIndex >= 0 ? organizations[curOrgIndex] : {};
        let userRole;

        if (userRecord.system_role && userRecord.system_role.toUpperCase() === UserRoles.ADMIN) {
            //Overwrite role if System Admin
            userRole = UserRoles.ADMIN;
        } else if (curOrg.sibling_role === UserRoles.ORG_ADMIN) {
            //Overwrite role if Admin to any sibling project
            userRole = UserRoles.ORG_ADMIN;
        } else {
            userRole = curOrg.role;
        }
        // userRole = "VIEWER"; //For testing only, remove when story is finished or FE is ready

        sessionStorage.setItem('userProjectRole', userRole);
        return userRole;
    }
};

export const isSupportUser = () => {
    // This is Deprecated. Added this check for legacy users with user.role = SUPPORT
    const userRole = sessionStorage.getItem('userRole');

    const systemRole = sessionStorage.getItem('systemRole');
    return userRole === UserRoles.SUPPORT || systemRole === UserRoles.SUPPORT;
};

export const getUrlParam = (param) => {
    let params = getUrlParams();
    return params[param] || false;
};

export const getUrlParams = () => {
    //Check to see if there is actually a query string
    //on the location. If not fall through the conditional
    //and return false;
    let idx;
    let len;
    let params = {};
    let qsHashes;
    if (location.search) {
        qsHashes = location.search.split('?')[1].split('&');
        for (idx = 0, len = qsHashes.length; idx < len; ++idx) {
            let param = qsHashes[idx].split('=');
            //have to account for trailing "="
            if (param.length > 2) {
                for (idx = 2; idx < param.length; idx++) {
                    if (param[idx] === '') {
                        param[1] += '=';
                    }
                }
            }
            params[param[0]] = param[1];
        }
        return params;
    }
    return false;
};

/**
 * Returns a string of first name / last name. Or if either are null,
 * will return email instead.
 * @param userRecord
 * @returns {string|*|trim}
 */
export const getUserFullName = (userRecord) => {
    return userRecord.hasOwnProperty('first_name') && userRecord.first_name && userRecord.last_name
        ? `${userRecord.first_name} ${userRecord.last_name}`
        : userRecord.email;
};

/**
 * Validates the given password against the current password policy. If there are validation errors, they will
 * be pushed in the errors array.
 *
 * @param password password to validate
 * @param errors array that will be filled with validation errors
 */
export const validatePassword = (password, errors) => {
    const minLength = 8;
    const hasUpperCase = /[A-Z]/;
    const hasLowerCase = /[a-z]/;
    const symbols = /\W/;
    const numbers = /\d/;

    if (!password || password.length < minLength) errors.push(`Password must be at least ${minLength} characters`);
    if (!hasUpperCase.test(password) || !hasLowerCase.test(password))
        errors.push(`Password must contain both upper and lower case letters`);
    if (!symbols.test(password)) errors.push(`Password must contain a special character`);
    if (!numbers.test(password)) errors.push(`Password must contain a number`);
};

/**
 * Handles API errors and dispatches a notification using the notifyError context.
 *
 * @param {Object} error - The error object (typically from a failed API call).
 * @param {Function} notifyError - Context function to show error notifications.
 * @param {string} [alternateMsg] - Optional fallback message if no specific error is matched.
 */
export const handleError = (error, notifyError, alternateMsg = 'An unexpected error occurred.') => {
    if (!error?.data) {
        notifyError({ message: alternateMsg });
        return;
    }

    const { error_code, message, resolution, validation_errors, extended_error } = error.data;
    let errorMessage = '';
    let timeout = 5000;

    if (error_code === 'validation_failure' && Array.isArray(validation_errors)) {
        for (const err of validation_errors) {
            errorMessage += `\n${err.message}`;
        }
        errorMessage = `${makeNiceName(error_code)}: ${errorMessage}`;
    }
    if (error_code === 'permission_denied') {
        errorMessage = `${alternateMsg} Permission denied.`;
    } else if (error_code === 'unexpected_error') {
        if (message && message.trim()) {
            const resolutionText = resolution ? ` ${resolution}` : '';
            errorMessage = `${message}${resolutionText}`;
        } else {
            errorMessage = alternateMsg;
        }
    } else {
        const resolutionText = resolution ? resolution : '';
        errorMessage = `${makeNiceName(error_code)}: ${message} ${resolutionText}`;
    }

    if (extended_error) {
        errorMessage += `\n\n${extended_error.label}: ${extended_error.message}`;
        if (extended_error.timeout) {
            timeout = extended_error.timeout;
        }
    }

    notifyError({ message: errorMessage || alternateMsg, duration: timeout / 1000 });
};

/**
 * Makes a human-friendly version of a supplied string.
 *
 * @param {string} rawName - The raw string (e.g., error code or system name).
 * @param {boolean} [makeCaps=false] - If true, uses special capitalization from capsMap.
 * @returns {string} - Human-readable version of the string.
 */
export function makeNiceName(rawName, makeCaps = false) {
    if (!rawName) return '';

    // Rewrite known system-specific codes
    const replacements = {
        rivermeadow_standalone: 'vsphere',
        azure_dr: 'Backup to Azure Blob',
        vsphere_dr: 'Disaster Recovery to vSphere Datastore',
        vsphere_offline_replicas: 'Disaster Recovery between Primary and DR sites',
        in_situ_osm: 'OS Modernization',
        in_situ_modernization: 'OS Modernization',
        in_situ_osm_restore: 'Revert from Snapshot',
        in_situ_modernization_restore: 'Revert from Snapshot',
        in_situ_osm_cleanup: 'Remove Snapshot',
        in_situ_modernization_cleanup: 'Remove Snapshot',
        differential_config: 'delta_config',
        differential: 'delta',
    };

    if (replacements.hasOwnProperty(rawName)) {
        return replacements[rawName];
    }

    const capsMap = {
        ip: 'IP',
        id: 'ID',
        url: 'URL',
        uri: 'URI',
        vdc: 'VDC',
        os: 'OS',
        sdm: 'SDM',
        dhcp: 'DHCP',
        http: 'HTTP',
        https: 'HTTPS',
        aws: 'AWS',
        vsphere: 'vSphere',
        gcp: 'Google Cloud',
        openshift: 'OpenShift',
        nc2: 'Nutanix NC2',
        hpe_vme: 'HPE VM Essentials',
        poweredon: 'Powered On',
        poweredoff: 'Powered Off',
        suspended: 'Suspended',
        success: 'Ready',
        bios: 'BIOS',
        uefi: 'UEFI',
        azure: 'Azure',
        azure_local: 'Azure Local',
        vpc: 'VPC',
    };

    return rawName
        .toLowerCase()
        .split('_')
        .map((word) => {
            if (makeCaps && capsMap[word]) {
                return capsMap[word];
            }
            return word.charAt(0).toUpperCase() + word.slice(1);
        })
        .join(' ');
}

/**
 * Gets the value of a localStorage var
 * @param storageVarName
 * @returns {value} || null (if it doesn't exist)
 */
export const getStorageVar = (storageVarName) => {
    return JSON.parse(localStorage.getItem(storageVarName)) || null;
};

/**
 * Removes a storage var
 * @param storageVarName
 */
export const removeStorageVar = (storageVarName) => {
    localStorage.removeItem(storageVarName);
};

/**
 * Sets a local storage var
 * @param storageVarName
 * @param value
 */
export const setStorageVar = (storageVarName, value) => {
    localStorage.setItem(storageVarName, JSON.stringify(value));
    return JSON.stringify(value);
};

/**
 If a given item exists in session storage, parse it into an object and return it.
@param {string} sessionVarName - the name of the item.
@returns {object} itemObject - the parsed object.

@TODO - Need to evaluate the usage of this function, because we should
be returning null if the item doesn't exist.
*/
export const getSessionVar = (sessionVarName) => {
    return JSON.parse(sessionStorage.getItem(sessionVarName)) || false;
};

/**
 Takes an object and saves it to session storage.
    @param {string} sessionVarName - name to use as a storage key.
    @param {object} value - the object passed in.
    @returns {string} JSON.stringify(value) - the stringified version of the object.
*/
export const setSessionVar = (sessionVarName, value) => {
    sessionStorage.setItem(sessionVarName, JSON.stringify(value));
    return JSON.stringify(value);
};
/**
 * Removes a session storage item
 * @param sessionVarName
 */
export const removeSessionVar = (sessionVarName) => {
    sessionStorage.removeItem(sessionVarName);
};

// If at least one entitlement in the array is not expired and disabled, return true
export const validateEntitlements = (entitlements) => {
    return entitlements.some((ent) => ent.state !== 'expired' && ent.state !== 'disabled');
};

/**
 * returned time diff in weeks between 2 UTC dateTime strings
 * @param ended
 * @param started (optional) if not provided, it will be today
 */
export const dateDiffInWeeks = (ended, started) => {
    return Math.round(dateDiff(ended, started) / 7);
};

/**
 * returned time diff in days between 2 UTC dateTime strings
 * @param ended
 * @param started (optional) if not provided, it will be today
 */
export const dateDiff = (ended, started) => {
    if (ended) {
        var diffMs;

        if (!started) {
            diffMs = Math.abs(moment(ended).diff(moment()));
        } else {
            diffMs = Math.abs(moment(ended).diff(moment(started)));
        }

        return Math.floor(diffMs / 1000 / 60 / (60 * 24));
    } else {
        return '0';
    }
};

/**
 * Rounds a float to two decimal places, returning a two-decimal FPN
 * @param {number} num - The number to round
 * @returns {number} - Rounded number
 */
export const roundToTwo = (num) => {
    return +(Math.round(num + 'e+2') + 'e-2');
};

/**
 * Correctly converts KiB to GiB
 * @param {number} kib - kibibytes
 * @param {boolean} wholeNum - default is rounded, wholenumber doesn't round. Useful for total storage calculations.
 * @returns {number} - Size in GiB
 */
export const getGiBfromKiB = (kib, wholeNum = false) => {
    const gib = (kib * 8192) / 8589934592;
    return !wholeNum ? roundToTwo(gib) : gib;
};

/**
 * Converts GiB to GB. Especially useful when GB is expected.
 * @param {number} gib - Size in GiB
 * @returns {number} - Size in GB
 */
export const getGBfromGIB = (gib) => {
    return gib * 1.073741824;
};

/**
 * Converts MiB to GiB.
 * @param {number} mib - Size in MiB
 * @returns {number} - Size in GiB
 */
export const getGiBfromMiB = (mib) => {
    return Math.ceil(mib / 1024);
};

/**
 * Converts MiB to GiB, rounded to one decimal place.
 * @param {number} mib - Size in MiB
 * @returns {number} - Size in GiB
 */
export const getGiBfromMiBOneDecimal = (mib) => {
    return Number((mib / 1024).toFixed(1));
};

// Converts GiB to MiB.
export const getMiBFromGiB = (gib) => {
    return Math.ceil(gib * 1024);
};

/**
 * Converts GiB to TiB.
 * @param {number} gib - Size in GiB
 * @returns {number} - Size in TiB
 */
export const getTiBfromGiB = (gib) => {
    return roundToTwo(gib / 1024);
};

export const getBytesfromMB = (MB) => {
    return MB * 1024 * 1024;
};

// Returns the numerical value from a file size string like "50MB" or "200 GB"
export const getFileSizefromString = (fileSizeString) => {
    if (!fileSizeString) return false;

    const match = fileSizeString.trim().match(/^(\d*\.?\d+)\s*([a-zA-Z]+)?$/);
    if (!match) {
        return false;
    }

    const value = parseFloat(match[1]);
    return value;
};

/**
 * Get VM-based target recommended sizing
 * @param source - source object
 * @returns {string}
 */
export const getVMRecommendedSizing = (source) => {
    if (
        source.collection_type == 'vm' &&
        source.attributes.vm_perf_metrics &&
        source.attributes.vm_perf_metrics.timestamps &&
        source.attributes.vm_perf_metrics.timestamps.length != 0
    ) {
        var recommended_cpu = source.attributes.cpu
            ? source.attributes.cpu.processors
                ? source.attributes.cpu.processors.length
                : 1
            : 1;
        var recommended_ram_gb = getGiBfromKiB(source.attributes.memory.total_kb);
        if (source.attributes.vm_perf_metrics.ram_95th_percentile) {
            recommended_ram_gb = getGiBfromKiB(source.attributes.vm_perf_metrics.ram_95th_percentile);
        }
        if (source.attributes.vm_perf_metrics.recommended_cpu) {
            recommended_cpu = source.attributes.vm_perf_metrics.recommended_cpu;
        }
        return recommended_cpu + 'vCPU, ' + recommended_ram_gb + 'GB RAM';
    } else {
        if (source.cloud_sizing && source.cloud_sizing.indexOf(':') > 0) {
            return source.cloud_sizing.split(':')[1];
        }
    }
};

/**
 * Converts mount info into a displayable form
 * @param {string} osType - Operating system type ('windows' or 'linux')
 * @param {Object} mounts - Mount information object
 * @param {boolean} allMountsEnabled - Set this to true to enable all mounts. This is primarily used for DM.
 * @param {boolean} displayDriveIcons - Whether to display drive icons
 * @param {boolean} dataOnlyMigration - Whether this is a data-only migration
 * @param {boolean} migrate_netapp_files - Whether migrating NetApp files
 * @returns {Array} - Array of display-ready mount objects
 */
export const getMountDisplayInfo = (
    osType,
    mounts,
    allMountsEnabled = false,
    displayDriveIcons = true,
    dataOnlyMigration = false,
    migrate_netapp_files = false,
) => {
    const displayMounts = [];
    const excludeFS = ['tmpfs', 'devtmpfs', 'sysfs', 'proc', 'devpts', 'cgroup', 'pstore'];
    const reservedMounts = {
        '/': true,
        '/bin': true,
        '/boot': true,
        '/boot/efi': true,
        '/dev': true,
        '/etc': true,
        '/home': true,
        '/lib': true,
        '/opt': true,
        '/proc': true,
        '/root': true,
        '/run': true,
        '/sbin': true,
        '/srv': true,
        '/tmp': true,
        '/usr': true,
        '/var': true,
    };
    const additionalNetAppReservedMounts = {
        '/boot/efi': true,
    };

    Object.entries(mounts).forEach(([mount, mountInfo]) => {
        const mountName = mount;
        const excludeMount =
            migrate_netapp_files === true &&
            ((osType === 'windows' && mount.toLowerCase() === 'c') ||
                (osType === 'linux' && (reservedMounts[mountName] || additionalNetAppReservedMounts[mountName])));

        if (
            !excludeMount &&
            (osType === 'windows' ||
                (osType === 'linux' &&
                    (((mountInfo.nature === 'disk' || mountInfo.nature === 'subvolume') &&
                        !excludeFS.includes(mountInfo.fs_type)) ||
                        (dataOnlyMigration && mountInfo.nature === 'network'))))
        ) {
            let displayMount = mount;
            if (displayDriveIcons === undefined || displayDriveIcons) {
                if (mount === '/' || mount.toLowerCase() === 'c') {
                    displayMount = '<HomeOutlined /> ' + mount; // Using emoji instead of FontAwesome
                } else {
                    displayMount = '<MenuOutlined /> ' + mount; // Using emoji instead of FontAwesome
                }
            }

            const processedMountInfo = {
                os_type: osType,
                mount: displayMount,
                mount_name: mountName,
                size: getGiBfromKiB(mountInfo.size_kb),
                free: getGiBfromKiB(mountInfo.free_kb),
                used: getGiBfromKiB(mountInfo.used_kb),
                size_gb: getGBfromGIB(getGiBfromKiB(mountInfo.size_kb)).toFixed(2),
                used_gb: getGBfromGIB(getGiBfromKiB(mountInfo.used_kb)).toFixed(2),
                mount_name_extra: '',
                fs_type: mountInfo.fs_type,
                hide_file_transfer: mountInfo.hide_file_transfer,
                disable_file_transfer: mountInfo.disable_file_transfer,
                transfer_type: mountInfo.transfer_type,
                bbt_supported: mountInfo.bbt_supported,
                mapped_path: '',
            };

            // allMountsEnabled makes sure that any drive can be selected. This is especially true for DM
            if (allMountsEnabled) {
                Object.assign(processedMountInfo, {
                    checked: false,
                    disabled: false,
                });
            } else {
                if (osType === 'windows') {
                    Object.assign(processedMountInfo, {
                        checked: 'checked',
                        disabled: mountName.toLowerCase() === 'c',
                    });
                } else if (osType === 'linux' && !reservedMounts[mountName]) {
                    Object.assign(processedMountInfo, {
                        checked: 'checked',
                        disabled: mountName === '/',
                    });
                } else {
                    Object.assign(processedMountInfo, {
                        checked: 'checked',
                        disabled: true,
                    });
                }
            }

            // Handle dataOnlyMigration use case separately
            if (dataOnlyMigration) {
                processedMountInfo.disabled = false;
                if (mountName.toLowerCase() === 'c' || (osType === 'linux' && reservedMounts[mountName])) {
                    processedMountInfo.checked = false;
                } else {
                    processedMountInfo.checked = true;
                }
            }
            displayMounts.push(processedMountInfo);
        }
    });

    // Sort the mounts before returning
    displayMounts.sort((a, b) => a.mount_name.localeCompare(b.mount_name));
    return displayMounts;
};

/**
 * Converts disk info into a displayable form
 * @param {Object} disks - Disk information object
 * @param {boolean} handle_dynamic_disks - Whether to handle dynamic disks
 * @param {Object} selected_mounts - Selected mount information
 * @param {boolean} filter_disks_by_mounts - Whether to filter disks by mounts
 * @returns {Array} - Array of display-ready disk objects
 */
export const getDiskDisplayInfo = (
    disks,
    handle_dynamic_disks = false,
    selected_mounts = {},
    filter_disks_by_mounts = false,
) => {
    const displayDisks = [];

    Object.entries(disks).forEach(([disk, diskInfo]) => {
        let skip_disk = false;

        if (handle_dynamic_disks === true && diskInfo.flags != null && diskInfo.flags.includes('dynamic_disk')) {
            // This is Windows specific, where we skip the dynamic disks when asked to handle them
            skip_disk = true;
        }

        if (filter_disks_by_mounts === true) {
            // This is Windows specific, and for basic disks
            // we match the mount device on disk.identifier only
            const hasMatchingMount = Object.values(selected_mounts).some(
                (mount) =>
                    mount.nature !== 'dynamic_disk' &&
                    mount.device != null &&
                    (mount.device === String(diskInfo.identifier) || mount.device === diskInfo.guid_identifier),
            );
            if (!hasMatchingMount) {
                skip_disk = true;
            }
        }

        if (!skip_disk) {
            const diskName = disk;
            let diskProvisioningType = '';
            if (diskInfo.thin_provisioned) {
                diskProvisioningType = 'thin provisioned';
            } else if (diskInfo.eagerly_scrub) {
                diskProvisioningType = 'thick provisioned eager zeroed';
            } else {
                diskProvisioningType = 'thick provisioned lazy zeroed';
            }

            const displayDisk = {
                disk: disk,
                disk_name: diskName,
                disk_label: diskInfo.label,
                disk_provisioning_type: diskProvisioningType,
                size: getGiBfromKiB(diskInfo.size_kb),
                disk_object_id: diskInfo.disk_object_id,
                checked: 'checked',
                disabled: false,
            };

            displayDisks.push(displayDisk);
        }
    });

    // Sort the disks
    displayDisks.sort((a, b) => a.disk_name.localeCompare(b.disk_name));

    // Add on disk for each selected dynamic mount point
    Object.entries(selected_mounts).forEach(([mount_name, mountInfo]) => {
        // This is Windows specific, where by default we create one disk per dynamic disk mount
        if (mountInfo.nature === 'dynamic_disk') {
            const displayDisk = {
                disk: {},
                disk_name: mount_name,
                disk_label: mount_name,
                size: getGiBfromKiB(mountInfo.size_kb),
                checked: 'checked',
                disabled: false,
            };

            displayDisks.push(displayDisk);
        }
    });

    return displayDisks;
};

/**
 * Formats a value for CSV export by escaping quotes and wrapping in quotes
 * @param {any} value - The value to format
 * @returns {string} - CSV-formatted value
 */
export function formatCSVValue(value) {
    const csvFormattedValue = (value ? value.toString() : '').replaceAll('"', '""');
    return '"' + csvFormattedValue + '"';
}

/**
 * Generates and downloads a CSV file from data array
 * @param {Array<Array>} csvData - 2D array of CSV data (rows and columns)
 * @param {string} fileName - Name of the file to download (without extension)
 */
export function csvFileGenerator(csvData, fileName) {
    let csvContent = '';
    csvData.forEach((row, i) => {
        csvContent += row.join(',') + '\n';
    });

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8,' });
    const objUrl = URL.createObjectURL(blob);
    const downloadLink = document.createElement('a');
    downloadLink.href = objUrl;
    downloadLink.download = fileName + '.csv';
    downloadLink.click();

    // Clean up
    URL.revokeObjectURL(objUrl);
}

/**
 * Gets user object from session storage
 * @returns {Object} - User object
 */
export function getUserObject() {
    return getSessionVar('user') || {};
}

/**
 * Validates if a string is a valid IPv4 address
 * @param {string} ip - The IP address to validate
 * @returns {boolean} - True if valid IPv4 address, false otherwise
 */
export function isValidIP(ip) {
    if (!ip || typeof ip !== 'string') return false;

    const parts = ip.split('.');
    if (parts.length !== 4) return false;

    return parts.every((part) => {
        const num = parseInt(part, 10);
        return part === num.toString() && num >= 0 && num <= 255;
    });
}

/**
 * Gets organization data from session storage
 * @returns {Object} - Organization object
 */
export function getOrganizationData() {
    const userOrg = sessionStorage.getItem('userOrg');
    const organizations = getSessionVar('organizations') || [];
    return organizations.find((org) => org.id === userOrg) || {};
}

export const retrieveApplianceNetworkName = (appliance) => {
    let result = null;

    if (appliance.hasOwnProperty('cloud_properties')) {
        if (appliance.cloud_properties && appliance.cloud_properties.hasOwnProperty('network')) {
            let networkConfig = appliance.cloud_properties.network;
            if (networkConfig.hasOwnProperty('interfaces') && networkConfig.interfaces.hasOwnProperty('eth0')) {
                result =
                    networkConfig.interfaces.eth0.hasOwnProperty('dv_switch_name') &&
                    networkConfig.interfaces.eth0.dv_switch_name
                        ? networkConfig.interfaces.eth0.dv_switch_name +
                          '/' +
                          networkConfig.interfaces.eth0.network_name
                        : networkConfig.interfaces.eth0.network_name;
            }
        } else if (appliance.cloud_properties && appliance.cloud_properties.hasOwnProperty('network_name')) {
            result = appliance.cloud_properties.network_name;
        }
    }

    return result;
};

export const convertToFlatNetworkProperties = (appliance) => {
    if (appliance && appliance.cloud_properties) {
        let network = appliance.cloud_properties.network;
        let ip_type = network.interfaces.eth0.ip_type;

        appliance.cloud_properties.network_ip_type = makeNiceName(ip_type, true);
        appliance.cloud_properties.network_name = retrieveApplianceNetworkName(appliance);

        if (ip_type === 'static') {
            appliance.cloud_properties.gateway = network.interfaces.eth0.netmask;
            if (network.hasOwnProperty('routes')) {
                appliance.cloud_properties.gateway = network.routes.default_gateway;
            }
            if (network.hasOwnProperty('dns')) {
                appliance.cloud_properties.primary_dns = network.dns.primary;
                appliance.cloud_properties.secondary_dns = network.dns.secondary;
            }
        }

        delete appliance.cloud_properties.network;
    }
};

/**
 * Compare two version strings and returns the major
 *
 * @param a { string }
 * @param b { string }
 * @returns Number  less than 0 if a < b
 *                  0 if a === b
 *                  more than 0 if a > b
 */
export const compareVersions = (a, b, use_major_minor_only) => {
    const re = /(\.0)+[^\.]*$/;

    // Ensure safe strings (handle null/undefined)
    const aParts = (a ?? '').toString().replace(re, '').split('.');
    const bParts = (b ?? '').toString().replace(re, '').split('.');

    let len = Math.min(aParts.length, bParts.length);
    if (use_major_minor_only === true) {
        len = 2;
    }

    let cmp = 0;
    for (let i = 0; i < len; i++) {
        cmp = parseInt(aParts[i], 10) - parseInt(bParts[i], 10);
        if (cmp !== 0) {
            return cmp;
        }
    }

    if (use_major_minor_only) {
        return cmp;
    }

    return aParts.length - bParts.length;
};

/**
 * Traverses an object path described by a
 * path string (e.g. appliance.cloud_properties.name),
 * then returns the value of the last one, which should be
 * a discrete value.
 *
 * @param obj
 * @param path
 * @returns {*}
 */
export const getValFromPath = (obj, path) => {
    let pathArr = path.split('.');
    let tmp = false;

    //Copy the first tmp
    tmp = obj[pathArr.shift()];
    //Now lop off the first entry from
    //the remaining array to traverse object

    if (tmp) {
        while (pathArr.length && typeof tmp === 'object' && tmp !== null) {
            tmp = tmp[pathArr.shift()];
        }
    }
    return !pathArr.length ? tmp : false;
};

/**
 * Check if the appliance is upgradeable
 *
 * @param appliance_version { string }
 * @param upgrade_version { string }
 * @returns Number less than 0 if appliance_version < upgrade_version
 * 0 if appliance_version === upgrade_version
 * more than 0 if appliance_version > upgrade_version
 */
export const isApplianceUpgradeableToVersion = (appliance_version, upgrade_version) => {
    return (
        compareVersions(appliance_version, CONFIG.CUTOFF_VERSION_FOR_PYTHON2, true) > 0 &&
        compareVersions(upgrade_version, CONFIG.CUTOFF_VERSION_FOR_PYTHON2, true) > 0
    );
};

/*
 * Checks if Appliance process is running
 */
export const isApplianceAvailable = (appliance) => {
    //if heart beat is true from appliance no need to look for process state
    var heartBeatStatus = getValFromPath(appliance, 'appliance.heartbeat_status');
    if (heartBeatStatus === true) {
        return true;
    }

    var processState = getValFromPath(appliance, 'appliance.process_state');
    if (processState === ApplianceStates.RUNNING) {
        return false;
    }
    return true;
};

/**
 * Check if appliance is ready
 */
export const isApplianceReady = (appliance, migrateByVM) => {
    if (!appliance) return false;

    // Check basic appliance readiness: state must be "success" and heartbeat must be true
    const isBasicReady = appliance.state === ApplianceStates.SUCCESS && appliance.heartbeat_status === true;

    if (migrateByVM) {
        // For VM migrations, also check source workers
        if (appliance.source_workers && appliance.source_workers.length) {
            const hasReadySourceWorker = appliance.source_workers.some((sw) => sw.heartbeat_status === true);
            return isBasicReady && hasReadySourceWorker;
        } else {
            // No source workers available for VM migration
            return false;
        }
    }

    return isBasicReady;
};

/**
 * Returns all organizations (is_project: false)
 * Each org includes its `default_project_id` if it has a child project named "Default".
 */
export const getOnlyOrganizations = (organizations = []) => {
    return organizations
        .filter((org) => !org.is_project)
        .map((org) => {
            var defaultProject = organizations.find(
                (p) => p.is_project && p.parent_organization_id === org.id && p.name === 'Default',
            );

            return {
                ...org,
                default_project_id: defaultProject ? defaultProject.id : null,
            };
        });
};

/**
 * Returns all projects (is_project: true)
 */
export const getOnlyProjects = (organizations = []) => {
    return organizations.filter((org) => org.is_project);
};
/**
 * Converts MSSQL upgrade options into displayable format
 * @param {Array} upgradeOptions - Array of upgrade option strings
 * @returns {Array} - Array of upgrade option objects with label and value
 */
export const getMSSQLUpgradeOptions = (upgradeOptions) => {
    const OPTION_TO_LABEL = {
        MSSQL2016: '2016',
        MSSQL2019: '2019',
        MSSQL2022: '2022',
        MSSQL2025: '2025',
    };

    const formattedOptions = [];
    if (upgradeOptions && Array.isArray(upgradeOptions)) {
        upgradeOptions.forEach((option) => {
            if (OPTION_TO_LABEL[option]) {
                formattedOptions.push({ label: OPTION_TO_LABEL[option], value: option });
            }
        });
    }
    return formattedOptions;
};

/**
 * Converts IP address to numeric value
 * @param {string} ip - The IP address to convert
 * @returns {number} - Numeric representation of the IP address
 */
export const ipToNumber = (ip) => {
    if (!ip) return 0;
    const parts = ip.split('.');
    if (parts.length !== 4) return 0;
    return parts.reduce((acc, part, index) => {
        return acc + (parseInt(part, 10) || 0) * Math.pow(256, 3 - index);
    }, 0);
};

/**
 * Converts MSSQL installed programs into a displayable form
 * @param {Array} sqlPrograms - Array of SQL program objects
 * @returns {Array} - Array of display-ready SQL instance objects
 */
export const getMSSQLInstancesDisplayInfo = (sqlPrograms) => {
    if (!sqlPrograms || !Array.isArray(sqlPrograms)) return [];

    const sqlInstances = sqlPrograms.map((sqlProgram) => {
        let displayName =
            sqlProgram.display_name?.replace(' Database Engine Services', '') +
            ' Instance: ' +
            sqlProgram.additional_info?.instance_name;

        if (sqlProgram.additional_info?.display_short_name) {
            displayName =
                sqlProgram.additional_info.display_short_name +
                ', Instance: ' +
                sqlProgram.additional_info?.instance_name;
        }

        return {
            id: sqlProgram.guid,
            instance_id: sqlProgram.additional_info?.instance_id,
            display_name: displayName,
            display_version: sqlProgram.display_version,
            display_short_version: sqlProgram.additional_info?.display_short_version,
            edition: sqlProgram.additional_info?.edition,
            service_pack: sqlProgram.additional_info?.service_pack,
            patch_level: sqlProgram.additional_info?.patch_level,
            instance_name: sqlProgram.additional_info?.instance_name,
            upgrade_options: getMSSQLUpgradeOptions(sqlProgram.upgrade_options),
        };
    });

    return sqlInstances.sort((left, right) => {
        if (left.display_short_version < right.display_short_version) {
            return -1;
        } else if (left.display_short_version > right.display_short_version) {
            return 1;
        }
        return 0;
    });
};

export const getVSphereVersionsList = function () {
    return vSphereVersions.sort(function (a, b) {
        return parseInt(b.hardware_version) - parseInt(a.hardware_version);
    });
};

// Get status bar color based on criteria, for Storage and Memory usage components
export const getStatusBarColor = (criteria) => {
    if (criteria === 'undersized' || criteria === 'oversized') {
        return `var(--amber-default)`;
    }

    if (criteria === 'ready') {
        return `var(--blue)`;
    }

    return 'transparent';
};

// Parse CPU data from JSON string and return the CPUs object
export const parseCpuData = (cpusJsonString) => {
    try {
        const parsed = cpusJsonString ? JSON.parse(cpusJsonString) : null;
        return parsed?.CPUs;
    } catch (e) {
        return null;
    }
};

export const parseDiskData = (disksJsonString) => {
    try {
        const parsedDisks = disksJsonString ? JSON.parse(disksJsonString) : null;
        return parsedDisks?.disks;
    } catch (e) {
        return null;
    }
};

export const parseRamData = (ramJsonString) => {
    try {
        const parsedRam = ramJsonString ? JSON.parse(ramJsonString) : null;
        return parsedRam?.memory;
    } catch (e) {
        return null;
    }
};

// Sort organizations and their projects
// This data is returned from the API already in the correct order.
// When we add a new one without making another API call, we need to insert it in the right place.
export const sortOrganizationsAndProjects = (orgs) => {
    const parents = orgs.filter((org) => !org.is_project);
    const projects = orgs.filter((org) => org.is_project);

    const sorted = [];
    parents.forEach((parent) => {
        sorted.push(parent);
        projects.filter((proj) => proj.parent_organization_id === parent.id).forEach((proj) => sorted.push(proj));
    });
    return sorted;
};

/**
 * Helper function to format a date string for display as M/D/YYYY.
 */
export const formatDate = (dateStr) => {
    if (!dateStr) return null;
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return null;
    return date.toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', year: 'numeric' });
};

/**
 * Helper function to format date to UTC in ISO format
 */
export const formatDateToUTC = (date) => {
    if (!date) return undefined;
    return new Date(date).toISOString().split('T')[0];
};


// Perform deep equality check between two objects, handling nested structures through recursion.
// Replacement for Lodash isEqual function
export const deepEqual = (a, b) => {
    if (a === b) {
        return true;
    }
    if (a === null || b === null || typeof a !== 'object' || typeof b !== 'object') {
        return false;
    }
    const keysA = Object.keys(a);
    const keysB = Object.keys(b);
    if (keysA.length !== keysB.length) {
        return false;
    }
    return keysA.every((key) => deepEqual(a[key], b[key]));
};
